import React from "react";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectMPSTopicFile, selectHeadTitle } from "../../features/counter/counterSlice";
import PropTypes from "prop-types";
import classNames from "classnames";
import styles from "./ModernBreadcrumb.module.scss";
import { removeSpecialCharacters } from "../../utils";
import useIsWebScreen from "../../hooks/useScreenSize";
import VersionIndicatorBanner from "../ModernImport/VersionIndicatorBanner";
import { useTableOfContents } from "../../hooks/tableOfContentsHooks/useTableOfContents";
/**
 * Detects intermediate section/chapter from topic title
 * Used to build proper hierarchical breadcrumbs
 */

const detectIntermediateSection = (topicTitle, guidelinePage) => {
  if (!topicTitle || !guidelinePage) return null;

  // Define section keywords for different guidelines with display names and URL slugs
  const sectionMappings = {
    Antibiotic: {
      pneumonia: { label: "Pneumonia", slug: "Pneumonia" },
      osteomyelitis: { label: "Osteomyelitis", slug: "Osteomyelitis" },
      endocarditis: {
        label: "Infective endocarditis",
        slug: "Infective_endocarditis",
      },
      sepsis: {
        label: "Sepsis and septic shock",
        slug: "Sepsis_and_septic_shock_Empirical_regimens",
      },
      perinatal: {
        label: "Perinatal infections",
        slug: "c_Perinatal_infections",
      },
      respiratory: {
        label: "Respiratory tract infections other than pneumonia",
        slug: "c_Respiratory_tract_infections_other_than_pneumonia",
      },
    },
    "Addiction Medicine": {
      "tobacco smoking": {
        label: "Tobacco smoking and nicotine dependence",
        slug: "AMG_Tobacco_smoking_and_nicotine_dependence",
      },
      nicotine: {
        label: "Tobacco smoking and nicotine dependence",
        slug: "AMG_Tobacco_smoking_and_nicotine_dependence",
      },
      alcohol: {
        label: "Alcohol: hazardous, harmful and dependent use",
        slug: "AMG_Alcohol_hazardous_harmful_and_dependent_use",
      },
      gaming: {
        label: "Addictive behaviours",
        slug: "AMG_Addictive_behaviours",
      },
      gambling: {
        label: "Addictive behaviours",
        slug: "AMG_Addictive_behaviours",
      },
    },
    // NOTE: add more guidelines as needed
  };

  const titleLower = topicTitle.toLowerCase();

  // Try exact match first, then try alternative names
  let sections = sectionMappings[guidelinePage];

  // Handle addiction guideline name variations
  if (
    !sections &&
    (guidelinePage === "Addiction" ||
      guidelinePage === "Addiction Medicine" ||
      guidelinePage === "Addiction_Medicine")
  ) {
    sections = sectionMappings["Addiction Medicine"];
  }

  if (sections) {
    for (const [keyword, sectionInfo] of Object.entries(sections)) {
      if (titleLower.includes(keyword)) {
        return sectionInfo;
      }
    }
  }

  return null;
};

const ModernBreadcrumb = ({ customPath = null, className = "" }) => {
  const location = useLocation();
  const { pathname, search } = location;
  const { isDesktop } = useIsWebScreen();
  const mpstopicfile = useSelector(selectMPSTopicFile);
  const headTitle = useSelector(selectHeadTitle);
  const tocContext = useTableOfContents();

  // Only apply scroll behavior on topic pages
  const isTopicPage = pathname.includes("/viewTopic") || search.includes("topicfile=");

  // Show only on screens 768px and wider (tablets and desktop)
  if (!isDesktop) {
    return null;
  }

  // Generate breadcrumb items based on current route
  const generateBreadcrumbItems = () => {
    if (customPath) {
      return customPath;
    }

    const items = [];
    const searchParams = new URLSearchParams(search);

    if (pathname.includes("viewTopic") || pathname.includes("topicTeaser")) {
      return generateTopicBreadcrumbs(searchParams);
    } else if (pathname.startsWith("/guidelines")) {
      const pathParts = pathname.split("/").filter(Boolean);

      // Special case: Pregnancy_and_Breastfeeding should be treated as a resource
      if (pathParts.length === 2 && pathParts[1] === "Pregnancy_and_Breastfeeding") {
        return generatePregnancyBreadcrumbs();
      }

      // Special case: /guidelines/GuidelineName/updates
      if (pathParts.length === 3 && pathParts[2] === "updates") {
        return generateUpdatesBreadcrumbs();
      }

      // If it's /guidelines/GuidelineName/TopicName, treat it as a topic page
      if (pathParts.length >= 3) {
        return generateTopicBreadcrumbs(searchParams);
      } else {
        // If it's /guidelines/GuidelineName, treat it as a guideline overview
        return generateGuidelineBreadcrumbs();
      }
    } else if (pathname.startsWith("/calculator")) {
      return generateCalculatorBreadcrumbs();
    } else if (pathname.startsWith("/resources")) {
      return generateResourceBreadcrumbs();
    }

    return items;
  };

  const generateTopicBreadcrumbs = (searchParams) => {
    const items = [];

    items.push({
      label: "Guidelines",
      path: "/guidelines",
      isClickable: true,
    });

    const pathParts = pathname.split("/").filter(Boolean);
    let guidelinePage = searchParams.get("guidelinePage");
    let topicFile = searchParams.get("topicfile");

    if (!guidelinePage && pathParts.length >= 2 && pathParts[0] === "guidelines") {
      guidelinePage = pathParts[1];
      if (pathParts.length >= 3) {
        topicFile = pathParts[2];
      }
    }

    // Add guideline level
    let guidelineClean = null;
    if (guidelinePage) {
      const cleanedGuideline = removeSpecialCharacters(guidelinePage);
      guidelineClean = cleanedGuideline ? cleanedGuideline.replace(/ /g, "_") : null;

      // Handle special case: Pregnancy_and_Breastfeeding maps to quicklinks
      let lookupKey = guidelineClean;
      if (guidelineClean === "Pregnancy_and_Breastfeeding") {
        lookupKey = "quicklinks";
      }

      const guidelineInfo = mpstopicfile[lookupKey];

      if (guidelineInfo) {
        items.push({
          label: guidelineInfo.text,
          path: `/guidelines/${guidelineClean}`,
          isClickable: true,
        });
      }
    }

    if (topicFile) {
      let topicLabel = null;

      // Try to find topic data in mpstopicfile using various key formats
      let topicInfo = mpstopicfile[topicFile];

      // If exact match not found, try alternative key formats for modern URLs
      if (!topicInfo && topicFile) {
        // Try with different prefixes/suffixes that might be used in the data
        const alternativeKeys = [
          topicFile.replace(/^AMG_/, ""), // Remove AMG_ prefix
          topicFile.replace(/^GIG_/, ""), // Remove GIG_ prefix
          topicFile.replace(/_/g, " "), // Replace underscores with spaces
          `c_${topicFile}`, // Add c_ prefix
          topicFile.toLowerCase(),
          topicFile.replace(/([A-Z])/g, "_$1").toLowerCase(), // Convert camelCase to snake_case
        ];

        for (const key of alternativeKeys) {
          if (mpstopicfile[key]) {
            topicInfo = mpstopicfile[key];

            break;
          }
        }
      }

      if (topicInfo && topicInfo.text) {
        // Use the reliable route-based data from mpstopicfile
        topicLabel = topicInfo.text;
      } else {
        // Different logic for viewTopic vs modern guideline URLs
        if (pathname.includes("viewTopic") || pathname.includes("topicTeaser")) {
          // For viewTopic URLs, headTitle is usually the most accurate
          if (headTitle) {
            topicLabel = headTitle;

            // Try to detect intermediate section from topic title for better breadcrumb hierarchy
            const intermediateSectionInfo = detectIntermediateSection(headTitle, guidelinePage);
            if (intermediateSectionInfo && guidelineClean) {
              // Use actual URL slug instead of converting display name
              items.push({
                label: intermediateSectionInfo.label,
                path: `/guidelines/${guidelineClean}/${intermediateSectionInfo.slug}`,
                isClickable: true,
              });
            }
          }
        } else {
          // For modern guideline URLs, extract title from URL to avoid stale headTitle
          // Remove a bunch of prefixes and fix some specific weirdly named files
          topicLabel = topicFile
            .replace(/^AMG_/, "")
            .replace(/^GIG_/, "")
            .replace(/^CVG_/, "")
            .replace(/^DMG/, "")
            .replace(/^C_DBG_/, "")
            .replace(/^c_DBG_/, "")
            .replace(/^toc_d1e2126/, "Rhinitis and rhinosinusitis")
            .replace(/^cough_0/, "Cough")
            .replace(/^c_/, "") // Remove c_ prefix
            .replace(/_/g, " ") // Replace underscores with spaces
            .replace(/([A-Z])/g, " $1") // Add spaces before capital letters
            .replace(/^[A-Z]{2,3}\s/, "") // Remove prefixes like "GIG "
            .trim();
          topicLabel = topicLabel.charAt(0).toUpperCase() + topicLabel.slice(1);
        }
      }

      if (tocContext.umbrellaNode && tocContext.umbrellaNode.id) {
        const decodedId = decodeURIComponent(tocContext.umbrellaNode?.id);
        const splitId = decodedId.split("/");
        const lastPage = splitId[splitId.length - 1];
        const formattedUmbrella = lastPage.replace(".dita", "");
        if (!items.find((item) => item.label === tocContext.umbrellaNode?.title)) {
          items.push({
            label: tocContext.umbrellaNode?.title,
            path: `/guidelines/${guidelineClean}/${formattedUmbrella}`,
            isClickable: true,
          });
        }
      }

      if (topicLabel) {
        const cleanedLabel = topicLabel
          .replace(/^[A-Z]\s*\|\s*[A-Z]\s+/, "") // Remove patterns like "G | G "
          .replace(/^[A-Z]{2,3}\s+/, "")
          .trim();

        items.push({
          label: cleanedLabel || topicLabel, // Fallback to original if cleaning results in empty string
          path: null,
          isClickable: false,
        });
      }
    }

    return items;
  };

  const generateGuidelineBreadcrumbs = () => {
    const items = [];
    const pathParts = pathname.split("/").filter(Boolean);

    items.push({
      label: "Guidelines",
      path: "/guidelines",
      isClickable: pathParts.length > 1,
    });

    if (pathParts.length > 1) {
      let guideline_key = pathParts[1]; // e.g., "Addiction_Medicine" or "Pregnancy_and_Breastfeeding"

      // Handle special case: Pregnancy_and_Breastfeeding maps to quicklinks
      let lookupKey = guideline_key;
      if (guideline_key === "Pregnancy_and_Breastfeeding") {
        lookupKey = "quicklinks";
      }

      const guidelineInfo = mpstopicfile[lookupKey];

      if (guidelineInfo) {
        items.push({
          label: guidelineInfo.text,
          path: null,
          isClickable: false,
        });
      } else {
      }
    }

    return items;
  };

  const generateUpdatesBreadcrumbs = () => {
    const items = [];
    const pathParts = pathname.split("/").filter(Boolean);

    // Add "Guidelines" as first breadcrumb
    items.push({
      label: "Guidelines",
      path: "/guidelines",
      isClickable: true,
    });

    if (pathParts.length > 1) {
      const guideline_key = pathParts[1]; // e.g., "Antibiotic" or "Cardiovascular"
      const guidelineInfo = mpstopicfile[guideline_key];

      if (guidelineInfo) {
        // Add guideline name as second breadcrumb (clickable - goes to overview)
        items.push({
          label: guidelineInfo.text,
          path: `/guidelines/${guideline_key}`,
          isClickable: true,
        });

        // Add "Practice changing updates" as final breadcrumb (not clickable - current page)
        items.push({
          label: "Practice changing updates",
          path: null,
          isClickable: false,
        });
      }
    }

    return items;
  };

  const generatePregnancyBreadcrumbs = () => {
    const items = [];

    items.push({
      label: "Resources",
      path: "/resources",
      isClickable: true,
    });

    items.push({
      label: "Pregnancy and breastfeeding",
      path: null,
      isClickable: false,
    });

    return items;
  };

  const generateCalculatorBreadcrumbs = () => {
    const items = [];
    const pathParts = pathname.split("/").filter(Boolean);

    items.push({
      label: "Calculators",
      path: "/calculator",
      isClickable: pathParts.length > 1,
    });

    if (pathParts.length > 1) {
      const calculatorNames = {
        ccbwcalc: "Creatinine clearance calculator for adults",
        gfrcalc: "Glomerular filtration rate (GFR) calculator for children",
        bsacalc: "Body surface area calculator",
        ibwcalc: "Ideal body weight calculator",
        lbwcalc: "Lean body weight calculator",
        idcalc: "Aminoglycoside initial dose calculator for adults",
      };

      const calculatorName = calculatorNames[pathParts[1]];
      if (calculatorName) {
        items.push({
          label: calculatorName,
          path: null,
          isClickable: false,
        });
      }
    }

    return items;
  };

  const generateResourceBreadcrumbs = () => {
    const items = [];
    const pathParts = pathname.split("/").filter(Boolean);

    items.push({
      label: "Resources",
      path: "/resources",
      isClickable: pathParts.length > 1,
    });

    if (pathParts.length > 1) {
      const resourceNames = {
        "antibiotic-summary": "Antibiotic prescribing in primary care - summary table",
        "surgical-prophylaxis": "Surgical prophylaxis - summary tables",
        "pregnancy-breastfeeding": "Pregnancy and breastfeeding",
        "tables-and-figures": "Tables and figures",
      };

      const resourceName = resourceNames[pathParts[1]];
      if (resourceName) {
        items.push({
          label: resourceName,
          path: null,
          isClickable: false,
        });
      }
    }

    return items;
  };

  const handleBreadcrumbClick = (path) => {
    if (path) {
      window.location.href = path;
    }
  };

  const breadcrumbItems = generateBreadcrumbItems();

  if (!breadcrumbItems || breadcrumbItems.length <= 1) {
    return null;
  }

  return (
    <nav
      className={classNames(styles.breadcrumb, className, isTopicPage && styles.topicPage)}
      aria-label="Breadcrumb navigation"
    >
      {breadcrumbItems.map((item, index) => (
        <React.Fragment key={index}>
          {index > 0 && (
            <span className={styles.separator} aria-hidden="true">
              /
            </span>
          )}
          {item.isClickable ? (
            <button
              className={styles.breadcrumbLink}
              onClick={() => handleBreadcrumbClick(item.path)}
              type="button"
            >
              {item.label}
            </button>
          ) : (
            <span>
              <span className={styles.breadcrumbCurrent}>{item.label}</span>
              <VersionIndicatorBanner />
            </span>
          )}
        </React.Fragment>
      ))}
    </nav>
  );
};

ModernBreadcrumb.propTypes = {
  customPath: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      path: PropTypes.string,
      isClickable: PropTypes.bool,
    }),
  ),
  className: PropTypes.string,
};

export default ModernBreadcrumb;
